import { ArrowUpRight, ExternalLink, Linkedin } from "lucide-react"

const posts = [
  {
    id: 1,
    title: "La acreditación documental no es solo un trámite",
    excerpt:
      "La gestión documental HSE debe evolucionar desde cumplimiento reactivo hacia inteligencia operacional trazable.",
    image: "/images/linkedin/post-1.webp",
    category: "Gestión Documental",
    date: "Junio 2026",
    readTime: "3 min",
    url: "https://www.linkedin.com/company/safeline-solutions",
  },
  {
    id: 2,
    title: "Cumplir no debería depender de archivos sueltos",
    excerpt:
      "La trazabilidad documental permite reducir desviaciones operacionales y fortalecer auditorías HSE.",
    image: "/images/linkedin/post-2.webp",
    category: "Compliance",
    date: "Junio 2026",
    readTime: "4 min",
    url: "https://www.linkedin.com/company/safeline-solutions",
  },
  {
    id: 3,
    title: "Cuando sube el combustible, también sube el riesgo operacional",
    excerpt:
      "Las decisiones operacionales impactan simultáneamente seguridad, costos y continuidad operacional.",
    image: "/images/linkedin/post-3.webp",
    category: "Operaciones",
    date: "Mayo 2026",
    readTime: "5 min",
    url: "https://www.linkedin.com/company/safeline-solutions",
  },
]

export default function LinkedInFeed() {
  return (
    <section className="relative overflow-hidden bg-[#07111A] py-24">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-teal-500/10 blur-3xl" />

        <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="mb-16 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-teal-500/20 bg-teal-500/10 px-4 py-2">
              <Linkedin className="h-4 w-4 text-teal-400" />

              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-300">
                SafeLine Insights
              </span>
            </div>

            <h2 className="text-4xl font-bold leading-tight text-white md:text-5xl">
              Reflexiones y análisis
              <span className="block text-teal-400">
                sobre gestión HSE moderna
              </span>
            </h2>

            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-400">
              Publicaciones técnicas enfocadas en prevención,
              cumplimiento, inteligencia operacional y transformación
              HSE aplicada a industria, minería y energía.
            </p>
          </div>

          <div>
            <a
              href="https://www.linkedin.com/company/safeline-solutions"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-6 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-teal-400/40 hover:bg-teal-500/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-400"
            >
              Ver LinkedIn Oficial

              <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </div>

        {/* Cards */}
        <div className="grid gap-8 lg:grid-cols-3">
          {posts.map((post) => (
            <a
              key={post.id}
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:border-teal-400/30 hover:bg-white/[0.05] hover:shadow-[0_20px_80px_rgba(20,184,166,0.15)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-400"
            >
              {/* Image */}
              <div className="relative overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  width={600}
                  height={400}
                  loading="lazy"
                  className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#07111A] via-transparent to-transparent opacity-70" />

                {/* Category */}
                <div className="absolute left-5 top-5">
                  <span className="rounded-full border border-teal-400/20 bg-[#07111A]/80 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-teal-300 backdrop-blur-md">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-7">
                {/* Meta */}
                <div className="mb-4 flex items-center gap-3 text-sm text-slate-500">
                  <span>{post.date}</span>

                  <span className="h-1 w-1 rounded-full bg-slate-600" />

                  <span>{post.readTime}</span>
                </div>

                {/* Title */}
                <h3 className="mb-4 text-2xl font-bold leading-tight text-white transition-colors duration-300 group-hover:text-teal-300">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="mb-8 leading-relaxed text-slate-400">
                  {post.excerpt}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span className="text-sm font-semibold text-teal-400">
                    Leer publicación
                  </span>

                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 transition-all duration-300 group-hover:border-teal-400/40 group-hover:bg-teal-500/10">
                    <ArrowUpRight className="h-4 w-4 text-white transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </div>

              {/* Hover Glow */}
              <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/[0.03] to-cyan-500/[0.03]" />
              </div>
            </a>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20">
          <div className="mx-auto max-w-4xl rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center backdrop-blur-sm">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-teal-400/20 bg-teal-500/10">
              <Linkedin className="h-8 w-8 text-teal-400" />
            </div>

            <h3 className="text-3xl font-bold text-white">
              Más análisis en LinkedIn
            </h3>

            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-400">
              Publicamos regularmente sobre prevención, cumplimiento y
              gestión HSE moderna. Síguenos para no perderte las próximas
              publicaciones.
            </p>

            <div className="mt-8 flex items-center justify-center">
              <a
                href="https://www.linkedin.com/company/safeline-solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-2xl bg-teal-500 px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-400 hover:shadow-[0_10px_40px_rgba(20,184,166,0.35)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-300"
              >
                Seguir en LinkedIn

                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}